import * as React from 'react';
import { IGroupedComboBoxItem } from '../GroupedComboBox/GroupedComboBox.types.shared';
import {
    IItemCommands,
    ItemMouseEventCallbackInternal,
    TouchEventCallbackInternal,
} from './GroupedComboBoxDropDown.types.internal';

const NavigationIdleDelay = 250; /* ms */

interface ITrackedItem {
    key: string;
    menuCommands: IItemCommands;
}

function createItemKey(item: IGroupedComboBoxItem, parentGroupKey?: string): string {
    return `${item.itemKey}-${parentGroupKey}`;
}

function resetTouchHoverRef(
    ref: React.MutableRefObject<ITrackedItem | undefined>,
    itemKey: string
): void {
    if (ref.current?.key !== itemKey) {
        ref.current?.menuCommands.setIsHover(false);
        ref.current = undefined;
    }
}

function updateTouchHoverRef(
    ref: React.MutableRefObject<ITrackedItem | undefined>,
    itemKey: string,
    menuCommands: IItemCommands
): void {
    ref.current?.menuCommands.setIsHover(false);
    ref.current = { key: itemKey, menuCommands };
    menuCommands.setIsHover(true);
}

export const useDelayHoverOpenSubmenu = (
    onMouseEnter?: ItemMouseEventCallbackInternal,
    onMouseLeave?: ItemMouseEventCallbackInternal,
    delay?: number
): [
    onMouseEnterCallback: ItemMouseEventCallbackInternal,
    onMouseLeaveCallback: ItemMouseEventCallbackInternal,
    onSubmenuOpen: (
        item: IGroupedComboBoxItem,
        menuCommands: IItemCommands,
        parentGroupKey?: string
    ) => void,
    onSubmenuClose: () => void,
    clearPending: () => void,
    onTouchStart: TouchEventCallbackInternal
] => {
    const timeoutId = React.useRef<number>();
    const pendingItemKey = React.useRef<string>();
    const openItem = React.useRef<ITrackedItem>();

    const touchHoveredItem = React.useRef<ITrackedItem>();
    const touchHoveredSubItem = React.useRef<ITrackedItem>();

    const clearPending = React.useCallback(() => {
        timeoutId.current && window.clearTimeout(timeoutId.current);
        timeoutId.current = undefined;
        pendingItemKey.current = undefined;
    }, []);

    const onTouchStart = React.useCallback(
        (
            item: IGroupedComboBoxItem,
            menuCommands: IItemCommands,
            isSubButton?: boolean,
            parentGroupKey?: string
        ) => {
            const itemKey = createItemKey(item, parentGroupKey);
            const itemRef = isSubButton ? touchHoveredSubItem : touchHoveredItem;
            if (itemRef.current?.key === itemKey) {
                return;
            }
            updateTouchHoverRef(itemRef, itemKey, menuCommands);
        },
        []
    );

    const onMouseEnterCallback = React.useCallback(
        (
            item: IGroupedComboBoxItem,
            ev?: React.MouseEvent<unknown>,
            menuCommands?: IItemCommands,
            isSubButton?: boolean,
            parentGroupKey?: string
        ) => {
            const itemKey = createItemKey(item, parentGroupKey);
            resetTouchHoverRef(touchHoveredItem, itemKey);
            resetTouchHoverRef(touchHoveredSubItem, itemKey);

            // Can this item affect showing or hiding the submenu?
            // L2 items don't affect hiding or showing submenus
            if (
                !isSubButton &&
                itemKey !== pendingItemKey.current &&
                itemKey !== openItem.current?.key
            ) {
                // Cancel previous timer if still running
                // Previous pending item is no longer pending due to cancelling the timer
                clearPending();

                const previousMenuCommands = openItem.current?.menuCommands;
                if (menuCommands) {
                    // Hovering over submenu button, schedule open menu
                    timeoutId.current = window.setTimeout(() => {
                        // Close previously opened menu
                        previousMenuCommands?.closeMenu();
                        // Open current button's menu
                        menuCommands?.openMenu();
                        // Track the new menu as the currently open item
                        openItem.current = { menuCommands, key: itemKey };

                        // The pending item is now open, so there is no pending item
                        timeoutId.current = undefined;
                        pendingItemKey.current = undefined;
                    }, delay ?? NavigationIdleDelay);

                    // Save reference to button we'll be opening so we don't try to open it again
                    // If opening this item gets cancelled, either the `else` case below will reset `openItem`
                    //    or the new submenu button hovered over will overwrite this.
                    pendingItemKey.current = itemKey;
                } else if (
                    itemKey !== pendingItemKey.current &&
                    itemKey === openItem.current?.key
                ) {
                    // We've moved off the open submenu button and back to it.
                    // Cancel the scheduled menu close in order to keep the open item open
                    clearPending();
                } else if (openItem.current) {
                    // Hovering over primary button, schedule closing the previously opened submenu if it exists
                    timeoutId.current = window.setTimeout(() => {
                        previousMenuCommands?.closeMenu();
                        openItem.current = undefined;
                        timeoutId.current = undefined;
                    }, delay ?? NavigationIdleDelay);
                    // We're closing the menu so there is no item pending open
                    pendingItemKey.current = undefined;
                }
            }
            onMouseEnter?.(item, ev);
        },
        [clearPending, delay, onMouseEnter]
    );

    const onMouseLeaveCallback = React.useCallback(
        (item, ev) => {
            clearPending();
            onMouseLeave?.(item, ev);
        },
        [clearPending, onMouseLeave]
    );

    // If we've opened a menu in a different manner we still need to update this state
    const onSubmenuOpen = React.useCallback(
        (item: IGroupedComboBoxItem, menuCommands: IItemCommands, parentGroupKey?: string) => {
            clearPending();
            openItem?.current?.menuCommands.closeMenu();
            openItem.current = {
                key: createItemKey(item, parentGroupKey),
                menuCommands: menuCommands,
            };
        },
        [clearPending]
    );

    const onSubmenuClose = React.useCallback(() => {
        clearPending();
        openItem.current = undefined;
    }, [clearPending]);

    return [
        onMouseEnterCallback,
        onMouseLeaveCallback,
        onSubmenuOpen,
        onSubmenuClose,
        clearPending,
        onTouchStart,
    ];
};
